import React, { useRef, useState } from 'react';
import { createTranscriptUpload, uploadRecordingToS3 } from '../api';

//const TRANSCRIPT_APPOINTMENT_ID = '18';

function formatRecordedAt(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i += 1) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function encodeWav(audioBuffer) {
  const numChannels = 1;
  const sampleRate = audioBuffer.sampleRate;
  const length = audioBuffer.length;
  const mixed = new Float32Array(length);
  const channelCount = audioBuffer.numberOfChannels;

  for (let i = 0; i < length; i += 1) {
    let sum = 0;
    for (let channel = 0; channel < channelCount; channel += 1) {
      sum += audioBuffer.getChannelData(channel)[i];
    }
    mixed[i] = sum / channelCount;
  }

  const buffer = new ArrayBuffer(44 + mixed.length * 2);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + mixed.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, mixed.length * 2, true);

  let offset = 44;
  for (let i = 0; i < mixed.length; i += 1, offset += 2) {
    const sample = Math.max(-1, Math.min(1, mixed[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return new Blob([view], { type: 'audio/wav' });
}

async function blobToWav(blob) {
  const audioContext = new AudioContext();
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    return encodeWav(audioBuffer);
  } finally {
    if (audioContext.state !== 'closed') {
      await audioContext.close();
    }
  }
}

const AudioRecorder = ({
  appointmentId,
  onRecordingComplete
}) => { 
  const TRANSCRIPT_APPOINTMENT_ID = appointmentId;
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [statusKind, setStatusKind] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const uploadUrlPromiseRef = useRef(null);

  const startRecording = async () => {
    try {
      setStatusKind('');
      setStatus('Requesting microphone...');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickRecorderMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;
      streamRef.current = stream;
      console.log(TRANSCRIPT_APPOINTMENT_ID);
      uploadUrlPromiseRef.current = createTranscriptUpload({
        appointmentId: TRANSCRIPT_APPOINTMENT_ID,
        recordedAt: formatRecordedAt(),
      }).then((data) => {
        if (!data?.presignedURL) {
          throw new Error('Upload URL missing from response');
        }
        return data.presignedURL;
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const recordedType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const recordedBlob = new Blob(chunksRef.current, { type: recordedType });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        await uploadToS3(recordedBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setStatusKind('recording');
      setStatus('Recording...');
    } catch (err) {
      console.error(err);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      setIsRecording(false);
      setStatusKind('error');
      setStatus(err?.name === 'NotAllowedError' ? 'Microphone permission denied' : 'Could not start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setStatusKind('');
      setStatus('Processing...');
    }
  };

  const uploadToS3 = async (blob) => {
    setIsUploading(true);
    setStatusKind('');
    setStatus('Uploading...');

    try {
      const presignedURL = await uploadUrlPromiseRef.current;
      if (!presignedURL) {
        throw new Error('Failed to get upload URL');
      }

      let wavBlob = blob;
      try {
        wavBlob = await blobToWav(blob);
      } catch (conversionError) {
        console.error(conversionError);
        wavBlob = new Blob([blob], { type: 'audio/wav' });
      };
     

      await uploadRecordingToS3(presignedURL, wavBlob).finally(()=>{
         onRecordingComplete(true);
      });
      setStatusKind('success');
      setStatus('Uploaded successfully!');
    } catch (err) {
      console.error(err);
      setStatusKind('error');
      setStatus(err?.message === 'Failed to get upload URL' ? 'Failed to get upload URL' : 'Upload failed');
    } finally {
      setIsUploading(false);
      mediaRecorderRef.current = null;
      uploadUrlPromiseRef.current = null;
    }
  };

  return (
<div className="record-controls">
  <button
    type="button"
    className={`btn btn-sm ${isRecording ? "btn-danger" : "btn-success"}`}
    onClick={isRecording ? stopRecording : startRecording} style={{backgroundColor: '#0b5ed7', color: 'white'}} 
    disabled={isUploading}
  >
    {isUploading
      ? "Uploading..."
      : isRecording
      ? "Stop Recording"
      : "Start Recording"}
  </button>

  {status ? (
    <p className={`record-status ${statusKind}`}>
      {status}
    </p>
  ) : null}
</div>


    
    // <div className="record-controls">
    //   <button
    //     type="button"
    //     className={`btn ${isRecording ? 'btn-danger' : 'btn-success'}`}
    //     onClick={isRecording ? stopRecording : startRecording}
    //     disabled={isUploading}
    //   >
    //     {isUploading ? 'Uploading...' : isRecording ? 'Stop Recording' : 'Start Recording'}
    //   </button>
    //   {status ? <p className={`record-status ${statusKind}`}>{status}</p> : null}
    // </div>

    
  );
}

export default AudioRecorder;
